import { nonenumerable } from 'shared/decorators';
import { IA_JOB_STATUS } from 'lbs-pi-platform-access/es/data-server/image-analysis/jobs/consts';
import { translate } from 'shared/services/I18nService';
import { store } from 'shared/stores/core/decorators';
import { isRegionOnCurrentIaLayer } from 'shared/stores/image-analysis/helpers';
import { createSyntheticKey } from 'shared/stores/image-analysis/ImageAnalysisStore';
import { IA_BATCH_STATUS } from 'shared/stores/image-analysis/jobs/Batch';

@store('IA_JOBS')
export class JobStore {
  getters = {
    // The jobState getter is composed by this and child objects. When fully composed it should look like this:
    /*
        jobState: { // (batch coordinator state)
            regions: {
                complete: [],
                running: [],
                scheduled: [],
                queued: []
            },
            batch: { // (Coordinator's running batch state)
                jobProgress: 0,
                jobStatus: IA_JOB_STATUS.QUEUED,
                status: IA_BATCH_STATUS.NOT_STARTED
            },
            scheduled: { ...Coordinator's _scheduled_ batch state }
        }
     */
    jobState ({get}) {
      const {syntheticKey} = get('IA_STATE');
      const coordinator = this.coordinatorsByIaKey[syntheticKey];
      return coordinator ? coordinator.state : null;
    },
    jobStatus () {
      if (!this.currentCoordinator) {
        return IA_JOB_STATUS.UNKNOWN;
      }
      // FYI, progress info is only available when job status is "Active"; the API will report as "%d complete"
      return this.getters.jobState.batch.jobStatus;
    },
    isJobActuallyRunning () {
      return this.getters.jobStatus > IA_JOB_STATUS.QUEUED && this.getters.jobStatus < IA_JOB_STATUS.COMPLETED;
    },
    getCoordinator: ({factory}) => (imageId, macroId) => {
      const key = createSyntheticKey(imageId, macroId, true);
      if (!this.coordinatorsByIaKey[key]) {
        this.coordinatorsByIaKey[key] = factory.create('IA_JOB_BATCH_COORDINATOR', imageId, macroId);
      }
      return this.coordinatorsByIaKey[key];
    },
    isCurrentLayerBeingProcessed: () => {
      if (!this.currentCoordinator) {
        return false;
      }
      return this.getters.isLayerBeingProcessed({id: this.currentCoordinator.jobParams.inputAnnotationId});
    },
    isLayerBeingProcessed: () => ({id}) => {
      const coordinators = Object.values(this.coordinatorsByIaKey);
      const coordinator = coordinators.find(c => c.jobParams.inputAnnotationId === id || c.jobParams.outputAnnotationId === id);
      const batch = coordinator ? coordinator.state.batch : null;

      if (batch) {
        const batchStatus = batch.status;
        return batchStatus > IA_BATCH_STATUS.NOT_STARTED && batchStatus < IA_BATCH_STATUS.FINISHED;
      }
      return false;
    }
  };

  @nonenumerable
  coordinatorsByIaKey = {};
  @nonenumerable
  currentCoordinator = null;

  // Creates a BatchCoordinator if it makes sense, or sets current batch to null
  IA_STATE_CHANGE ({factory, get}) {
    const {syntheticKey, imageId, macroId} = get('IA_STATE');

    // If no image / macro is selected there's nothing to do.
    if (!syntheticKey) {
      return;
    }

    // Pluck an existing coordinator from the cache if present and resume it, or create a new one.
    let coordinator = this.coordinatorsByIaKey[syntheticKey];
    if (!coordinator) {
      coordinator = factory.create('IA_JOB_BATCH_COORDINATOR', imageId, macroId);
      this.coordinatorsByIaKey[syntheticKey] = coordinator;
    } else {
      coordinator.resume();
    }

    // If the coordinator has changed, update refs
    if (this.currentCoordinator !== coordinator) {
      // update new coordinator
      this.currentCoordinator = coordinator;
      // Note: We do not tell the old coordinator to stop polling because when it completes we need to reinitialize
      //       the annotations so that any output regionIds that have changed can be sync'd with DataServer.
    }
  }

  IA_BEFORE_REGION_SAVE (di, region) {
    if (this.currentCoordinator) {
      this.currentCoordinator.addRegion(region);
    }
  }

  IA_REGION_SAVED (di, region) {
    if (this.currentCoordinator) {
      this.currentCoordinator.updateRegion(region);
    }
  }

  // IA_REGION_DELETED triggers on output region deleted. We may need to rethink that. Maybe publish IA_INPUT_LAYER
  // and IA_OUTPUT_LAYER deleted events?
  REGION_DELETED ({get}, {region}) {
    if (this.currentCoordinator && isRegionOnCurrentIaLayer(get, region)) {
      this.currentCoordinator.removeRegion(region);
    }
  }

  LAYER_REMOVED (store, {layer}) {
    Object.values(this.coordinatorsByIaKey).forEach((coordinator) => {
      if (coordinator.isMyLayer(layer)) {
        coordinator.removeAllRegions();
        coordinator.pause();
      }
    });
  }

  IA_JOB_ERROR ({get}, details) {
    const {uScope} = get('USCOPE');
    uScope.notifications.error(translate('ia.job-failed-message', details), 0);
  }

  IA_CHECK_FOR_RUNNING_JOBS ({get}, {regions}) {
    // remap IA "region data" to actual annotation regions
    const {allRegions} = get('ANNOTATIONS/currentState');
    regions = regions.map(r => allRegions[r.id]);

    if (this.currentCoordinator) {
      this.currentCoordinator.checkForRunningJobs(regions);
    }
  }

  IA_ADD_JOB () {
    if (this.currentCoordinator) {
      this.currentCoordinator.startBatch();
    }
  }

  IA_CANCEL_RUNNING_JOB () {
    if (this.currentCoordinator) {
      this.currentCoordinator.cancelRunningBatch();
    }
  }

  IA_CANCEL_SCHEDULED_JOB () {
    if (this.currentCoordinator) {
      this.currentCoordinator.cancelScheduledBatch();
    }
  }

  IA_RUN_REANALYSIS ({get}, {region}) {
    const {imageId} = region;
    const annotations = get('ANNOTATIONS');
    const outputLayer = annotations.getLayer(region);
    const inputLayer = annotations.getInputLayer(outputLayer);
    const {macroId} = outputLayer;

    const coordinator = this.getters.getCoordinator(imageId, macroId);
    coordinator.startReanalysisBatch(region, inputLayer);
  }
}



// WEBPACK FOOTER //
// ./src/shared/stores/image-analysis/jobs/JobStore.js